import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, Copy } from 'lucide-react';
import { LEVEL_LABELS, PathwayTemplate } from '../../types/pathway';
import { moduleById } from '../../data/skillModules';
import { formatHours, totalHours } from '../../utils/pathwayMatching';
interface ProvenPathwaysProps {
  templates: PathwayTemplate[];
  onUseTemplate: (template: PathwayTemplate) => void;
}

/**
 * Pre-built sequences anyone can start from. Deliberately kept outside the
 * saved-pathways folder so removing that feature leaves these intact.
 */
export function ProvenPathways({
  templates,
  onUseTemplate
}: ProvenPathwaysProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  return <section aria-labelledby="proven-pathways-heading" className="mt-10" data-id="element-1737" data-mp="19ukrroh8m378">
      <div className="mb-4" data-id="element-1738" data-mp="3952b8tks2mfn">
        <h2 id="proven-pathways-heading" className="text-xl font-bold text-gray-900" data-id="element-1739" data-mp="39381mbeyigqv">
          Proven pathways
        </h2>
        <p className="mt-1 max-w-xl text-sm text-gray-600" data-id="element-1740" data-mp="kitc44gk4bi8">
          Pre-built sequences your team already sells. Duplicate one and edit it
          for your prospect.
        </p>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm" data-id="element-1741" data-mp="amqe9asqmz13">
        <div className="overflow-x-auto" data-id="element-1742" data-mp="fk01qkwkh0ma">
          <table className="w-full table-fixed sm:min-w-[760px]" data-id="element-1743" data-mp="fmvxq3outf83">
            <caption className="sr-only" data-id="element-1744" data-mp="13bf94s37a84e">
              Proven pathways — expand a row to see its modules
            </caption>
            <colgroup data-id="element-1745" data-mp="188no6jt1xw1f">
              <col data-id="element-1746" data-mp="tkkjreunqseq" />
              <col className="w-[200px]" data-id="element-1747" data-mp="tl2pr374gkzu" />
              <col className="w-[110px]" data-id="element-1748" data-mp="tk198j7c76aq" />
              <col className="w-[190px]" data-id="element-1749" data-mp="yjdx979y2jew" />
            </colgroup>
            <thead data-id="element-1750" data-mp="yhszz9w8wmdc">
              <tr className="border-b border-gray-200 bg-gray-50" data-id="element-1751" data-mp="1m989pnqcq5ym">
                <th className="whitespace-nowrap px-3 py-3 text-left text-sm font-semibold text-gray-700 sm:px-6" data-id="element-1752" data-mp="1m7nc1p8qq6wj">
                  Pathway
                </th>
                <th className="whitespace-nowrap px-3 py-3 text-left text-sm font-semibold text-gray-700 sm:px-6" data-id="element-1753" data-mp="20w6y19twwk94">
                  Audience
                </th>
                <th className="whitespace-nowrap px-3 py-3 text-center text-sm font-semibold text-gray-700 sm:px-6" data-id="element-1754" data-mp="1vv16vgfwh51p">
                  Modules
                </th>
                <th className="whitespace-nowrap px-3 py-3 text-right text-sm font-semibold text-gray-700 sm:px-6" data-id="element-1755" data-mp="1mvmfmerwsqg5">
                  <span className="sr-only" data-id="element-1756" data-mp="1hydymhihysgh">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody data-id="element-1757" data-mp="1d4jcl7ff61e8">
              {templates.map(template => {
              const expanded = expandedId === template.id;
              return <React.Fragment key={template.id} data-id="element-1758" data-mp="139hpeh4pf9ea">
                    <tr className="border-b border-gray-100 transition-colors hover:bg-gray-50" data-id="element-1759" data-mp="z6lbv4u0u3h7">
                      <td className="px-3 py-4 sm:px-6" data-id="element-1760" data-mp="u7i6fja4pv8p">
                        <button type="button" onClick={() => setExpandedId(expanded ? null : template.id)} aria-expanded={expanded} className="flex items-start gap-2 text-left" data-id="element-1761" data-mp="u80b94auxhs8">
                          <ChevronDown size={16} aria-hidden="true" className={`mt-0.5 flex-shrink-0 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} data-id="element-1762" data-mp="3jv4stq00x9wv" />
                          <span data-id="element-1763" data-mp="2w3poxsslmkji">
                            <span className="block text-sm font-semibold text-gray-900" data-id="element-1764" data-mp="30vqy35ugzv5b">
                              {template.name}
                            </span>
                            <span className="mt-0.5 block text-xs text-gray-500" data-id="element-1765" data-mp="23g3cdv1e85ki">
                              {template.summary}
                            </span>
                          </span>
                        </button>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700 sm:px-6" data-id="element-1766" data-mp="9yfi75c4livz">
                        {LEVEL_LABELS[template.audience]}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-center text-sm text-gray-700 sm:px-6" data-id="element-1767" data-mp="iyknn9xre77i">
                        {template.moduleIds.length}
                        <span className="ml-1 text-xs text-gray-500" data-id="element-1768" data-mp="xpbgmlb37cc7">
                          · {formatHours(totalHours(template.moduleIds))}
                        </span>
                      </td>
                      <td className="px-3 py-4 sm:px-6" data-id="element-1769" data-mp="16lssxrv6tg9j">
                        <div className="flex items-center justify-end" data-id="element-1770" data-mp="12nczb54lp9n5">
                          <button type="button" onClick={() => onUseTemplate(template)} className="flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-[#30718D] transition-colors hover:text-[#265a70]" data-id="element-1771" data-mp="38f05wunbvls9">
                            <Copy size={14} data-id="element-1772" data-mp="2yaa18h0ji3kc" />
                            Duplicate
                          </button>
                        </div>
                      </td>
                    </tr>

                    <AnimatePresence initial={false} data-id="element-1773" data-mp="2phzrt1xgy245">
                      {expanded && <tr data-id="element-1774" data-mp="2akph3usnh09h">
                          <td colSpan={4} className="border-b border-gray-100 bg-white p-0" data-id="element-1775" data-mp="2akfodzg80f03">
                            <motion.div initial={{
                        opacity: 0,
                        height: 0
                      }} animate={{
                        opacity: 1,
                        height: 'auto'
                      }} exit={{
                        opacity: 0,
                        height: 0
                      }} transition={{
                        duration: 0.2,
                        ease: [0.23, 1, 0.32, 1]
                      }} className="overflow-hidden" data-id="element-1776" data-mp="21mdtm1n2yil9">
                              <ol className="my-2 ml-6 space-y-1.5 border-l-2 border-[#30718D]/20 pl-4 pr-3 sm:ml-10 sm:pr-6" data-id="element-1777" data-mp="3ugd3slxo0vye">
                                {template.moduleIds.map((moduleId, index) => {
                            const module = moduleById(moduleId);
                            if (!module) return null;
                            return <li key={moduleId} className="flex items-baseline gap-3 text-sm" data-id="element-1778" data-mp="h42zje01r84s" data-mpi={index}>
                                      <span className="w-4 flex-shrink-0 text-xs font-bold text-[#30718D]" data-id="element-1779" data-mp="7cp4pjj1iwhz">
                                        {index + 1}
                                      </span>
                                      <span className="font-medium text-gray-900" data-id="element-1780" data-mp="100b4sht27j03">
                                        {module.name}
                                      </span>
                                      <span className="text-xs text-gray-500" data-id="element-1781" data-mp="1ccwrvq4pi434">
                                        {formatHours(module.durationHours)} ·{' '}
                                        {module.category}
                                      </span>
                                    </li>;
                          })}
                              </ol>
                            </motion.div>
                          </td>
                        </tr>}
                    </AnimatePresence>
                  </React.Fragment>;
            })}
            </tbody>
          </table>
        </div>
      </div>
    </section>;
}